import { css } from 'aphrodite';
import * as React from 'react';
import { useAdminTranscriptAskMutation } from '../../../queries/Admin/Calendar/useAdminTranscriptAskMutation';
import { ITranscriptQAExchange } from '../../../viewmodels/CalendarViewModels';
import WorkingLionImageUrl from '../../../web/assets/aiAssistantThinking.png';
import { Avatar2 } from '../../../web/components/Avatar2';
import { SparklesIcon } from '../../../web/components/svgs/icons/SparklesIcon';
import { AIMarkdownResponse } from '../../../web/components-v2/ai/AiMarkdownResponse';
import {
	ChatConversation,
	ChatConversationContent,
	ChatConversationScrollButton,
} from '../../../web/components-v2/ai/ChatConversation';
import { ChatInput } from '../../../web/components-v2/ai/ChatInput';
import { ThinkingIndicator } from '../../../web/components-v2/ai/ThinkingIndicator';
import {
	Dialog,
	DialogBackdrop,
	DialogBody,
	DialogHeader,
	DialogPopup,
	DialogPortal,
	DialogTitle,
} from '../../../web/components-v2/Dialog';
import { AIChatAssistantMessage, AIChatUserMessage } from '../../../web/containers/templates/AssistantChat';
import { styleSheet } from './styles';

const MAX_CONVERSATION_EXCHANGES = 10;

const ASK_LEV_THINKING_MESSAGES = ['Reading the transcript', 'Finding the answer', 'Checking my notes', 'Almost there'];

interface IProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	transcriptContent: string;
	meetingTopic?: string;
	attendeeNames?: string[];
}

export const AskLevDialog: React.FC<IProps> = ({
	open,
	onOpenChange,
	transcriptContent,
	meetingTopic,
	attendeeNames,
}) => {
	const [conversationHistory, setConversationHistory] = React.useState<ITranscriptQAExchange[]>([]);
	const [pendingQuestion, setPendingQuestion] = React.useState<string | null>(null);
	const [lastError, setLastError] = React.useState<string | null>(null);

	const askMutation = useAdminTranscriptAskMutation();

	// Reset state when dialog closes
	React.useEffect(() => {
		if (!open) {
			setConversationHistory([]);
			setPendingQuestion(null);
			setLastError(null);
			askMutation.reset();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const isAtCap = conversationHistory.length >= MAX_CONVERSATION_EXCHANGES;

	const handleSubmit = React.useCallback(
		async (question: string) => {
			if (isAtCap) return;

			setLastError(null);
			setPendingQuestion(question);

			try {
				const result = await askMutation.mutateAsync({
					transcriptContent,
					question,
					attendeeNames,
					conversationHistory,
				});

				setConversationHistory(prev => [...prev, { question, answer: result.answer }]);
				setPendingQuestion(null);
			} catch {
				setLastError(question);
				setPendingQuestion(null);
			}
		},
		[transcriptContent, attendeeNames, conversationHistory, askMutation, isAtCap]
	);

	const handleRetry = React.useCallback(() => {
		if (lastError) {
			const question = lastError;
			setLastError(null);
			handleSubmit(question);
		}
	}, [lastError, handleSubmit]);

	const title = meetingTopic ? `Ask Lev: ${meetingTopic}` : 'Ask Lev';

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogPortal>
				<DialogBackdrop />
				<DialogPopup showCloseButton styles={[styleSheet.popup]}>
					<DialogHeader styles={[styleSheet.header]}>
						<div className={css(styleSheet.headerStart)}>
							<Avatar2 imgSrc={WorkingLionImageUrl} fallbackText='Leo Avatar' styleDeclaration={styleSheet.leoAvatar} />
							<SparklesIcon className={css(styleSheet.sparklesIcon)} />
							<DialogTitle styles={[styleSheet.headerTitle]}>{title}</DialogTitle>
						</div>
					</DialogHeader>
					<DialogBody styles={[styleSheet.body]}>
						<ChatConversation styles={[styleSheet.conversation]}>
							<ChatConversationContent styles={[styleSheet.conversationContent]}>
								<div className={css(styleSheet.spacer)} />

								{/* Initial greeting */}
								<AIChatAssistantMessage>
									Ask me anything about this meeting transcript. I&apos;ll answer based on what was discussed and
									include direct quotes when possible.
								</AIChatAssistantMessage>

								{/* Completed exchanges */}
								{conversationHistory.map((exchange, i) => (
									<React.Fragment key={i}>
										<AIChatUserMessage>{exchange.question}</AIChatUserMessage>
										<AIChatAssistantMessage>
											<AIMarkdownResponse>{exchange.answer}</AIMarkdownResponse>
										</AIChatAssistantMessage>
									</React.Fragment>
								))}

								{/* Pending question (in-flight) */}
								{pendingQuestion && (
									<>
										<AIChatUserMessage>{pendingQuestion}</AIChatUserMessage>
										<AIChatAssistantMessage isThinking>
											<ThinkingIndicator messages={ASK_LEV_THINKING_MESSAGES} />
										</AIChatAssistantMessage>
									</>
								)}

								{/* Error state */}
								{lastError && !pendingQuestion && (
									<>
										<AIChatUserMessage>{lastError}</AIChatUserMessage>
										<AIChatAssistantMessage>
											<div className={css(styleSheet.errorContainer)}>
												<span>Sorry, something went wrong.</span>
												<button className={css(styleSheet.retryButton)} onClick={handleRetry}>
													Try Again
												</button>
											</div>
										</AIChatAssistantMessage>
									</>
								)}

								{/* Cap reached message */}
								{isAtCap && !pendingQuestion && (
									<div className={css(styleSheet.capMessage)}>
										Conversation limit reached. Close and reopen to start a new conversation.
									</div>
								)}
							</ChatConversationContent>
							<ChatConversationScrollButton />
						</ChatConversation>

						<div className={css(styleSheet.inputContainer)}>
							<ChatInput
								placeholder='Ask a question about this transcript...'
								onSubmit={handleSubmit}
								streamingInProgress={askMutation.isPending || isAtCap}
								supportsImages={false}
							/>
						</div>
					</DialogBody>
				</DialogPopup>
			</DialogPortal>
		</Dialog>
	);
};
